import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Mail, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/tenant/forgot-password", { email });
      setSent(true);
      if (r.data.stub_token_dev) setDevToken(r.data.stub_token_dev);
      toast.success("Check your email (or backend logs in dev mode)");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center grid-bg px-4 relative">
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-orange-500/10 blur-3xl" />
      <div className="relative w-full max-w-md glass-strong rounded-lg p-8 f360-border-gradient">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg f360-gradient flex items-center justify-center"><Flame className="w-5 h-5 text-gray-900" strokeWidth={2.5} /></div>
          <div>
            <h1 className="text-xl font-extrabold f360-gradient-text">Reset Password</h1>
            <p className="text-xs text-gray-500">We'll send a reset link</p>
          </div>
        </div>
        {!sent ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input required type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="pl-10 bg-gray-50 border-gray-200 h-12" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 f360-gradient border-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Send Reset Link"}
            </Button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-lg p-4 text-sm">
              If an account exists for <b>{email}</b>, you'll receive a reset link shortly.
            </div>
            {devToken && (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <span className="text-orange-400 font-bold">DEV MODE:</span> Reset link →{" "}
                <Link to={`/reset-password?token=${devToken}`} className="text-orange-400 underline">Click here to reset</Link>
              </div>
            )}
          </div>
        )}
        <Link to="/login" className="text-xs text-orange-400 hover:text-pink-400 mt-6 flex items-center gap-1"><ArrowLeft className="w-3 h-3" strokeWidth={2.5} />Back to sign in</Link>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tenant/reset-password", { token: params.get("token"), password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch { toast.error("Reset failed — link may have expired"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center grid-bg px-4">
      <div className="w-full max-w-md glass-strong rounded-lg p-8 f360-border-gradient">
        <h1 className="text-xl font-extrabold f360-gradient-text mb-4">Set new password</h1>
        <form onSubmit={submit} className="space-y-4">
          <Input required type="password" minLength={8} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-gray-50 border-gray-200 h-12" />
          <Button type="submit" disabled={busy} className="w-full h-12 f360-gradient border-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Update Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
